import { render, h } from 'vue'
import { createSharedComposable } from '@vueuse/core'
import Taboola from '../../components/AdOps/Taboola.vue'
import { getFallbackPlacementWidget } from '../../utils/ad-ops/adFunctions'
import loadScriptAsync from '../../utils/loadScriptAsync'
import debug from '../../utils/console'
const logger = debug.withTag('composables:useTaboolaAds')

declare global {
  interface Window {
    _taboola: Record<string, unknown>[]
  }
}

export type TaboolaEventDetail = {
  baseMode: string
  container: string
  itemCount: number
  placement: string
  variant: null
}
export type TaboolaEvent = {
  isTrusted: boolean
  bubbles: boolean
  cancelBubble: boolean
  cancelable: boolean
  composed: boolean
  currentTarget: null
  defaultPrevented: boolean
  detail: TaboolaEventDetail
  eventPhase: number
  returnValue: boolean
  srcElement: null
  target: null
  timeStamp: number
  type: string
}
const console = debug.withTag('useTaboolaAds')

/**
 * Extract the brand/advertiser name from a Taboola tile element.
 */
export function getAdBrandFromTile(tileElement: Element | null): string {
  if (!tileElement) return ''

  const item =
    tileElement.querySelector('.branding-inner') ??
    tileElement.querySelector('.branding')

  if (item instanceof HTMLElement) {
    return item.innerText || item.textContent || ''
  }

  return item?.textContent || ''
}

let fallbackCount = 0

const useTaboolaAds = createSharedComposable(() => {
  const taboolaPublisherName = useAppConfig().adops.taboola.publisherName

  const { $assertive } = useNuxtApp()
  const tagType = $assertive?.analytics?.custom?.custom_1 || ''

  const isHeadLoadedPromise =
    getPageType() === 'homepage'
      ? loadScriptAsync(
          'head',
          {
            textContent: `window._taboola = window._taboola || [];
          _taboola.push({
            homepage:'auto',
            custom_tracking:'sub_id=${tagType}'
          });
          !function (e, f, u, i) { if (!document.getElementById(i)){ e.async = 1; e.src = u; e.id = i; f.parentNode.insertBefore(e, f); } }
            (document.createElement('script'), document.getElementsByTagName('script')[0], '//cdn.taboola.com/libtrc/${taboolaPublisherName}/loader.js', 'tb_loader_script');
            if(window.performance && typeof(window.performance.mark) == 'function') {window.performance.mark('tbl_ic');}`,
            key: 'taboola-homepage-header-script',
          },
          {
            id: 'taboola-homepage-header-script',
            type: 'text/javascript',
            referrerpolicy: 'no-referrer-when-downgrade',
            async: true,
            onload: () => console.debug('Taboola script loaded.'),
            onerror: () => console.error('Taboola script failed to load.'),
          }
        )
      : loadScriptAsync(
          'head',
          {
            textContent: `window._taboola = window._taboola || [];
          _taboola.push({
            article:'auto',
            custom_tracking:'sub_id=${tagType}'
          });
          !function (e, f, u, i) { if (!document.getElementById(i)){ e.async = 1; e.src = u; e.id = i; f.parentNode.insertBefore(e, f); } }
            (document.createElement('script'), document.getElementsByTagName('script')[0], '//cdn.taboola.com/libtrc/${taboolaPublisherName}/loader.js', 'tb_loader_script');
            if(window.performance && typeof(window.performance.mark) == 'function') {window.performance.mark('tbl_ic');}`,
            key: 'taboola-article-header-script',
          },
          {
            id: 'taboola-article-header-script',
            type: 'text/javascript',
            referrerpolicy: 'no-referrer-when-downgrade',
            async: true,
            onload: () => console.debug('Taboola script loaded.'),
            onerror: () => console.error('Taboola script failed to load.'),
          }
        )

  const isBodyLoadedPromise = loadScriptAsync(
    'body',
    {
      textContent: `window._taboola = window._taboola || [];
        _taboola.push({flush: true});`,
      key: 'taboola-homepage-body-script',
    },
    {
      id: 'taboola-homepage-body-script',
      type: 'text/javascript',
      async: true,
      onload: () => console.debug('Taboola body script loaded.'),
      onerror: () => console.error('Taboola body script failed to load.'),
    }
  )

  const triggerTaboolaRenderedEvent = async (
    mode: string,
    taboolaContainerName: string,
    taboolaPlacementName: string,
    callbackFn: () => void
  ): Promise<void> => {
    try {
      await isHeadLoadedPromise
      await isBodyLoadedPromise
    } catch (error) {
      logger.warn(
        'Failed to load Taboola SDK, continuing without Taboola ads',
        error
      )
      return
    }

    callbackFn()

    window._taboola = window._taboola || []
    window._taboola.push({
      mode: mode,
      container: taboolaContainerName,
      placement: taboolaPlacementName,
      target_type: 'mix',
    })
  }

  const fireRenderCallback = (
    placement: string,
    executed: Ref<boolean>,
    callbackFn: () => void
  ): Promise<void> => {
    return new Promise<void>((resolve) => {
      window._taboola.push({
        listenTo: 'render',
        handler: function (e: TaboolaEvent) {
          const eventPlacement = e.detail.placement
          // Match exact placement name, or strip Taboola's feed suffix
          // (e.g., "article_content_below_button | Card 1" → base matches)
          const isMatch =
            eventPlacement === placement ||
            eventPlacement?.startsWith?.(placement + ' | ')
          if (isMatch) {
            executed.value = true
            callbackFn()
            resolve()
          }
        },
      })
    })
  }

  const callRenderTaboolaWidgets = async () => {
    await isHeadLoadedPromise
    await isBodyLoadedPromise
    window._taboola = window._taboola || []
    window._taboola.push({ notify: 'newPageLoad' })
    window._taboola.push({ flush: true })
  }

  // No `newPageLoad` notify — that would count an extra Taboola pageview.
  const flushTaboolaWidgets = async () => {
    await isHeadLoadedPromise
    await isBodyLoadedPromise
    window._taboola = window._taboola || []
    window._taboola.push({ flush: true })
  }

  // Renders a Taboola ad in a placement div. Used for programmatic ad fallback.
  const renderFallbackTaboolaAd = async (
    placement: string,
    element: Element | null
  ): Promise<void> => {
    const widgetProps = getFallbackPlacementWidget(placement, 'taboola')

    if (!widgetProps?.containerName || !widgetProps.mode || !element) return

    // A placement repeats down the feed, and Taboola container ids must be unique
    const containerName = `${widgetProps.containerName}-fallback${fallbackCount++}`
    // append fallback to the end of class to prog ads will no longer be mapped to the placement
    const newPlacementName = `${widgetProps.placement}-fallback`
    element.classList.remove(placement)
    element.classList.add(newPlacementName)

    // Erase the innerHTML of the placement div to clean scripts and other elements added by AY integration
    element.innerHTML = ''
    try {
      const { vueApp } = useNuxtApp()
      const taboolaNode = h(Taboola, {
        ...widgetProps,
        containerName,
        placement: newPlacementName,
      })
      taboolaNode.appContext = vueApp._context
      // Insert the taboola ad wrapper into the placement div
      render(taboolaNode, element)

      await nextTick()
      await flushTaboolaWidgets()
    } catch (err) {
      logger.error(
        `[Native-Fallback]: Error rendering fallback Taboola ad component ${placement}`,
        err
      )
    }
  }

  return {
    triggerTaboolaRenderedEvent,
    fireRenderCallback,
    callRenderTaboolaWidgets,
    renderFallbackTaboolaAd,
  }
})

export default useTaboolaAds
